import IClonable from "../types/IClonable";
import IEquatable from "../types/IEquatable";

const TICKS_PER_MILLISECOND = 1;
const TICKS_PER_SECOND = 1000;
const TICKS_PER_MINUTE = 60000;
const TICKS_PER_HOUR = 3600000;
const TICKS_PER_DAY = 86400000;

function pad(value: number): string {
	return ("0" + value).slice(-2);
}

function toISOString(ts: TimeSpan): string {
	const negative = ts.getTicks() < 0;
	const absTicks = Math.abs(ts.getTicks());

	const days = (absTicks / TICKS_PER_DAY) | 0;
	const hours = ((absTicks / TICKS_PER_HOUR) | 0) % 24;
	const minutes = ((absTicks / TICKS_PER_MINUTE) | 0) % 60;
	const seconds = ((absTicks / TICKS_PER_SECOND) | 0) % 60;
	const ms = absTicks % TICKS_PER_SECOND;

	const prefix = negative ? "-" : "";
	const time = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
	const msStr = ms > 0 ? `.${("00" + ms).slice(-3)}` : "";

	if (days > 0) {
		return `${prefix}${days}.${time}${msStr}`;
	}
	return `${prefix}${time}${msStr}`;
}

function parseString(value: string): number {
	let negative = false;
	let str = value;

	if (str.startsWith("-")) {
		negative = true;
		str = str.substring(1);
	}

	let days = 0;
	let timePart = str;

	const dotBeforeColon = str.indexOf(".");
	const colonIndex = str.indexOf(":");
	if (dotBeforeColon !== -1 && (colonIndex === -1 || dotBeforeColon < colonIndex)) {
		days = parseInt(str.substring(0, dotBeforeColon), 10);
		timePart = str.substring(dotBeforeColon + 1);
	}

	const [hoursStr, minutesStr, maybeSeconds] = timePart.split(":");
	let seconds = 0;
	let milliseconds = 0;
	if (maybeSeconds != null) {
		const [sec, ms] = maybeSeconds.split(".");
		seconds = parseInt(sec, 10);
		//fracción de segundo: los dígitos se escalan por posición (".5" = 500 ms, .NET manda hasta 7)
		if (ms != null && ms.length > 0) milliseconds = Math.round(Number("0." + ms) * 1000);
	}

	const hours = parseInt(hoursStr, 10);
	const minutes = parseInt(minutesStr, 10);

	const ticks = days * TICKS_PER_DAY
		+ (isNaN(hours) ? 0 : hours) * TICKS_PER_HOUR
		+ (isNaN(minutes) ? 0 : minutes) * TICKS_PER_MINUTE
		+ (isNaN(seconds) ? 0 : seconds) * TICKS_PER_SECOND
		+ (isNaN(milliseconds) ? 0 : milliseconds) * TICKS_PER_MILLISECOND;

	return negative ? -ticks : ticks;
}


export class TimeSpan implements IEquatable<TimeSpan>, IClonable<TimeSpan> {
	private static _zero: TimeSpan | null = null;
	private readonly _ticks: number;

	private constructor(ticks: number) {
		this._ticks = ticks;
	}

	get [Symbol.toStringTag](): string {
		return `TimeSpan ${toISOString(this)}`;
	}

	[Symbol.toPrimitive](hint: string): number | string {
		if (hint === "number") {
			return this._ticks;
		}

		return toISOString(this);
	}

	/**
	 * Returns a new TimeSpan whose value is the sum of this instance and the specified TimeSpan.
	 * @param other The TimeSpan to add.
	 * @returns A new TimeSpan representing the sum.
	 */
	add(other: TimeSpan): TimeSpan {
		return new TimeSpan(this._ticks + other._ticks);
	}

	/**
	 * Adds the specified number of days to the value of this instance.
	 * @param days The number of days to add. Can be negative.
	 * @returns A new TimeSpan with the added days.
	 */
	addDays(days: number): TimeSpan {
		return new TimeSpan(this._ticks + days * TICKS_PER_DAY);
	}

	/**
	 * Adds the specified number of hours to the value of this instance.
	 * @param hours The number of hours to add. Can be negative.
	 * @returns A new TimeSpan with the added hours.
	 */
	addHours(hours: number): TimeSpan {
		return new TimeSpan(this._ticks + hours * TICKS_PER_HOUR);
	}

	/**
	 * Adds the specified number of minutes to the value of this instance.
	 * @param minutes The number of minutes to add. Can be negative.
	 * @returns A new TimeSpan with the added minutes.
	 */
	addMinutes(minutes: number): TimeSpan {
		return new TimeSpan(this._ticks + minutes * TICKS_PER_MINUTE);
	}

	/**
	 * Adds the specified number of seconds to the value of this instance.
	 * @param seconds The number of seconds to add. Can be negative.
	 * @returns A new TimeSpan with the added seconds.
	 */
	addSeconds(seconds: number): TimeSpan {
		return new TimeSpan(this._ticks + seconds * TICKS_PER_SECOND);
	}

	/**
	 * Creates a new object that is a copy of the current instance.
	 * @returns A new TimeSpan object that is a copy of this instance.
	 */
	clone(): TimeSpan {
		return new TimeSpan(this._ticks);
	}

	/**
	 * Compares this instance to a specified TimeSpan.
	 * @param other The TimeSpan to compare to.
	 * @returns A signed number: negative if less, 0 if equal, positive if greater, 1 if other is null/undefined.
	 */
	compareTo(other: TimeSpan | null | undefined): number {
		if (other == null) {
			return 1;
		}

		return this._ticks - other._ticks;
	}

	/**
	 * Determines whether the specified TimeSpan is equal to this instance.
	 * @param other The TimeSpan to compare.
	 * @returns true if equal; otherwise, false.
	 */
	equals(other: TimeSpan | null | undefined): boolean {
		if (other == null) {
			return false;
		}

		return this._ticks === other._ticks;
	}

	/**
	 * Returns the days component of the time interval.
	 * @returns The day component.
	 */
	getDays(): number {
		return (this._ticks / TICKS_PER_DAY) | 0;
	}

	/**
	 * Returns the hours component of the time interval.
	 * @returns The hour component, expressed as a value between -23 and 23.
	 */
	getHours(): number {
		return ((this._ticks / TICKS_PER_HOUR) | 0) % 24;
	}

	/**
	 * Returns the milliseconds component of the time interval.
	 * @returns The milliseconds component, expressed as a value between -999 and 999.
	 */
	getMilliseconds(): number {
		return this._ticks % TICKS_PER_SECOND;
	}

	/**
	 * Returns the minutes component of the time interval.
	 * @returns The minute component, expressed as a value between -59 and 59.
	 */
	getMinutes(): number {
		return ((this._ticks / TICKS_PER_MINUTE) | 0) % 60;
	}

	/**
	 * Returns the seconds component of the time interval.
	 * @returns The seconds component, expressed as a value between -59 and 59.
	 */
	getSeconds(): number {
		return ((this._ticks / TICKS_PER_SECOND) | 0) % 60;
	}

	/**
	 * Returns the number of ticks (milliseconds) that represent this TimeSpan.
	 * @returns The total ticks.
	 */
	getTicks(): number {
		return this._ticks;
	}

	/**
	 * Returns the total number of days represented by this instance.
	 * @returns The total days, including fractional days.
	 */
	getTotalDays(): number {
		return this._ticks / TICKS_PER_DAY;
	}

	/**
	 * Returns the total number of hours represented by this instance.
	 * @returns The total hours, including fractional hours.
	 */
	getTotalHours(): number {
		return this._ticks / TICKS_PER_HOUR;
	}

	/**
	 * Returns the total number of minutes represented by this instance.
	 * @returns The total minutes, including fractional minutes.
	 */
	getTotalMinutes(): number {
		return this._ticks / TICKS_PER_MINUTE;
	}

	/**
	 * Returns the total number of seconds represented by this instance.
	 * @returns The total seconds, including fractional seconds.
	 */
	getTotalSeconds(): number {
		return this._ticks / TICKS_PER_SECOND;
	}

	/**
	 * Returns a new TimeSpan whose value is the negation of this instance.
	 * @returns A new TimeSpan with the negated value.
	 */
	negate(): TimeSpan {
		return new TimeSpan(-this._ticks);
	}

	/**
	 * Returns a new TimeSpan whose value is the absolute duration of this instance.
	 * @returns A new TimeSpan with the absolute value.
	 */
	duration(): TimeSpan {
		return new TimeSpan(Math.abs(this._ticks));
	}

	/**
	 * Returns a new TimeSpan representing the difference between this instance and another.
	 * @param other The TimeSpan to subtract.
	 * @returns A new TimeSpan representing the difference.
	 */
	subtract(other: TimeSpan): TimeSpan {
		return new TimeSpan(this._ticks - other._ticks);
	}

	/**
	 * Converts to a culture-invariant string: D.hh:mm:ss or hh:mm:ss (with optional .mmm).
	 * @returns An ISO-style duration string.
	 */
	toJSON(): string {
		return toISOString(this);
	}

	/**
	 * Converts to a culture-invariant string: D.hh:mm:ss or hh:mm:ss (with optional .mmm).
	 * @returns An ISO-style duration string.
	 */
	toString(): string {
		return toISOString(this);
	}

	/**
	 * Creates a TimeSpan from individual time components.
	 * @param days The number of days. Defaults to 0.
	 * @param hours The number of hours. Defaults to 0.
	 * @param minutes The number of minutes. Defaults to 0.
	 * @param seconds The number of seconds. Defaults to 0.
	 * @param milliseconds The number of milliseconds. Defaults to 0.
	 * @returns A new TimeSpan instance.
	 */
	public static fromComponents(days: number = 0, hours: number = 0, minutes: number = 0, seconds: number = 0, milliseconds: number = 0): TimeSpan {
		return new TimeSpan(
			days * TICKS_PER_DAY
			+ hours * TICKS_PER_HOUR
			+ minutes * TICKS_PER_MINUTE
			+ seconds * TICKS_PER_SECOND
			+ milliseconds * TICKS_PER_MILLISECOND
		);
	}

	/**
	 * Creates a TimeSpan from a culture-invariant string (D.hh:mm:ss.mmm or hh:mm:ss.mmm).
	 * Supports an optional leading "-" for negative values.
	 * @param value The string to parse.
	 * @returns A new TimeSpan instance.
	 */
	public static fromIsoString(value: string): TimeSpan {
		return new TimeSpan(parseString(value));
	}

	/**
	 * Creates a TimeSpan from the specified number of ticks (milliseconds).
	 * @param ticks The number of ticks.
	 * @returns A new TimeSpan instance.
	 */
	public static fromTicks(ticks: number): TimeSpan {
		return new TimeSpan(ticks);
	}

	/**
	 * Creates a TimeSpan representing the specified number of days.
	 * @param days The number of days.
	 * @returns A new TimeSpan instance.
	 */
	public static fromDays(days: number): TimeSpan {
		return new TimeSpan(days * TICKS_PER_DAY);
	}

	/**
	 * Creates a TimeSpan representing the specified number of hours.
	 * @param hours The number of hours.
	 * @returns A new TimeSpan instance.
	 */
	public static fromHours(hours: number): TimeSpan {
		return new TimeSpan(hours * TICKS_PER_HOUR);
	}

	/**
	 * Creates a TimeSpan representing the specified number of minutes.
	 * @param minutes The number of minutes.
	 * @returns A new TimeSpan instance.
	 */
	public static fromMinutes(minutes: number): TimeSpan {
		return new TimeSpan(minutes * TICKS_PER_MINUTE);
	}

	/**
	 * Creates a TimeSpan representing the specified number of seconds.
	 * @param seconds The number of seconds.
	 * @returns A new TimeSpan instance.
	 */
	public static fromSeconds(seconds: number): TimeSpan {
		return new TimeSpan(seconds * TICKS_PER_SECOND);
	}

	/**
	 * Returns a TimeSpan representing zero duration.
	 * @returns A TimeSpan with value 0.
	 */
	public static zero(): TimeSpan {
		return this._zero ??= new TimeSpan(0);
	}
}
